import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wifi, WifiOff, Download, RefreshCw, X, Smartphone, Share, PlusSquare } from 'lucide-react';
import { usePWA } from '../hooks/usePWA';

export function OfflineIndicator() {
  const { isOnline } = usePWA();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isOnline && !dismissed) {
      setShow(true);
    } else if (isOnline) {
      if (show) {
        setTimeout(() => setShow(false), 3000);
      }
      setDismissed(false);
    }
  }, [isOnline, dismissed, show]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className={`fixed top-0 left-0 right-0 z-[100] px-4 py-3 flex items-center justify-center gap-3 text-sm font-medium ${
          isOnline ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'
        }`}
      >
        {isOnline ? (
          <>
            <Wifi className="w-4 h-4" />
            <span>Connexion retablie</span>
          </>
        ) : (
          <>
            <WifiOff className="w-4 h-4" />
            <span>Mode hors ligne</span>
            <button onClick={() => { setDismissed(true); setShow(false); }} className="p-1 hover:bg-white/20 rounded">
              <X className="w-4 h-4" />
            </button>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

// Floating Install Button for Mobile - Samsung Compatible
export function MobileInstallButton() {
  const { canInstall, promptInstall, isInstalled } = usePWA();
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isSamsung, setIsSamsung] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor;
    const iOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const android = /android/i.test(userAgent);
    const samsung = /samsung|SM-|Galaxy/i.test(userAgent);
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    setIsIOS(iOS);
    setIsAndroid(android);
    setIsSamsung(samsung);
    setIsMobile(mobile || window.innerWidth < 768);

    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    const wasDismissed = localStorage.getItem('pwa-fab-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed);
      if (Date.now() - dismissedTime > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('pwa-fab-dismissed');
      } else {
        setDismissed(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-fab-dismissed', Date.now().toString());
    setShowModal(false);
  };

  const handleInstallClick = () => {
    // Always show modal - safer for Samsung devices
    setShowModal(true);
  };

  if (isInstalled || dismissed || !isMobile || isStandalone) return null;

  // FAB supprimé - L'install se fait uniquement via le prompt navigateur natif
  // Le bouton "+" central dans la navigation suffit pour les actions principales
  return (
    <>
      {/* Instructions Modal - Gardé pour les cas où l'utilisateur cherche à installer */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-[200] flex items-end justify-center"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-white w-full max-w-lg rounded-t-3xl overflow-hidden max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-rm-primary to-rm-secondary p-6 text-white sticky top-0">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Smartphone className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl">Installer l'application</h3>
                    <p className="text-white/80 text-sm">BatiAssist</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* iOS Instructions */}
                {isIOS && (
                  <div className="space-y-4">
                    <p className="text-gray-700 font-semibold text-center mb-4">Sur Safari :</p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">Appuyez sur Partager</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Share className="w-5 h-5 text-blue-500" />
                            <span className="text-sm text-gray-500">Le carre avec la fleche</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">"Sur l'ecran d'accueil"</p>
                          <div className="flex items-center gap-2 mt-1">
                            <PlusSquare className="w-5 h-5 text-gray-600" />
                            <span className="text-sm text-gray-500">Faites defiler</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">Appuyez sur "Ajouter"</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Samsung Android Instructions */}
                {isAndroid && isSamsung && (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl mb-4">
                      <p className="text-amber-800 text-sm font-semibold text-center">
                        Samsung Galaxy : utilisez le menu Chrome
                      </p>
                    </div>
                    <p className="text-gray-700 font-semibold text-center">Sur Chrome :</p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">Ouvrez le menu</p>
                          <p className="text-sm text-gray-500">Appuyez sur <span className="font-bold text-lg">⋮</span> en haut a droite</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">"Ajouter a l'ecran d'accueil"</p>
                          <p className="text-sm text-gray-500">Pas "Installer l'application"</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">Confirmez "Ajouter"</p>
                          <p className="text-sm text-gray-500">L'icone apparait sur l'ecran</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                      <p className="text-xs text-gray-600 text-center">
                        Cette methode evite les alertes de securite Samsung Knox
                      </p>
                    </div>
                  </div>
                )}

                {/* Other Android */}
                {isAndroid && !isSamsung && (
                  <div className="space-y-4">
                    <p className="text-gray-700 font-semibold text-center">Sur Chrome :</p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">Menu Chrome <span className="font-bold">⋮</span></p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">"Installer l'application"</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">Confirmez</p>
                        </div>
                      </div>
                    </div>
                    {canInstall && (
                      <button
                        onClick={async () => {
                          const installed = await promptInstall();
                          if (installed) handleDismiss();
                        }}
                        className="w-full mt-4 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl shadow-lg active:scale-[0.98]"
                      >
                        <Download className="w-5 h-5 inline mr-2" />
                        Installer directement
                      </button>
                    )}
                  </div>
                )}

                {/* Unknown device */}
                {!isIOS && !isAndroid && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="font-bold mb-2">iPhone</p>
                      <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                        <li>Safari &gt; Partager</li>
                        <li>"Sur l'ecran d'accueil"</li>
                        <li>Ajouter</li>
                      </ol>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="font-bold mb-2">Android</p>
                      <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                        <li>Chrome &gt; Menu ⋮</li>
                        <li>"Ajouter a l'accueil"</li>
                        <li>Ajouter</li>
                      </ol>
                    </div>
                  </div>
                )}

                {/* Benefits */}
                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-center gap-4 text-xs text-gray-500">
                  <span>Acces rapide</span>
                  <span>•</span>
                  <span>Hors ligne</span>
                  <span>•</span>
                  <span>Securise</span>
                </div>
              </div>

              {/* Buttons */}
              <div className="px-6 pb-8 sticky bottom-0 bg-white">
                <button onClick={() => setShowModal(false)} className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl">
                  Fermer
                </button>
                <button onClick={handleDismiss} className="w-full mt-2 py-2 text-gray-400 text-sm">
                  Ne plus afficher
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function InstallPromptBanner() {
  const { canInstall, promptInstall, isInstalled } = usePWA();
  const [dismissed, setDismissed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) setDismissed(true);
    setIsMobile(window.innerWidth < 768);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) handleDismiss();
  };

  if (!canInstall || dismissed || isInstalled || isMobile) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-4 right-4 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-50 hidden sm:block"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-rm-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
          <Download className="w-6 h-6 text-rm-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm">Installer l'application</h3>
          <p className="text-xs text-gray-500 mt-1">Acces rapide depuis ton bureau</p>
          <div className="flex gap-2 mt-3">
            <button onClick={handleInstall} className="px-4 py-2 bg-rm-primary text-white text-sm font-medium rounded-lg hover:bg-rm-primary/90">
              Installer
            </button>
            <button onClick={handleDismiss} className="px-4 py-2 text-gray-600 text-sm font-medium hover:bg-gray-100 rounded-lg">
              Plus tard
            </button>
          </div>
        </div>
        <button onClick={handleDismiss} className="p-1 hover:bg-gray-100 rounded-lg">
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </motion.div>
  );
}

export function UpdateAvailableBanner() {
  const { isUpdateAvailable, updateServiceWorker } = usePWA();

  if (!isUpdateAvailable) return null;

  // Masquer la bannière de mise à jour sur mobile (trop intrusive)
  // La mise à jour se fait automatiquement au prochain chargement
  return null;
  
  /* Ancienne version avec bannière - désactivée
  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-0 left-0 right-0 z-[100] bg-blue-600 text-white px-4 py-3 flex items-center justify-center gap-3 text-sm"
    >
      <RefreshCw className="w-4 h-4" />
      <span>Nouvelle version disponible</span>
      <button onClick={updateServiceWorker} className="px-3 py-1 bg-white text-blue-600 rounded-lg font-medium">
        Mettre a jour
      </button>
    </motion.div>
  );
  */
}

export function PWAComponents() {
  return (
    <>
      <OfflineIndicator />
      {/* MobileInstallButton désactivé - bouton "Ajouter à l'accueil" */}
      {/* <MobileInstallButton /> */}
      <InstallPromptBanner />
      <UpdateAvailableBanner />
    </>
  );
}
